import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Cookie, Calendar, Mail, Building, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mockData } from './mock';

export const CookiePolicy = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50/30 py-12 sm:py-16 lg:py-20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <div className="mb-8">
          <button
            onClick={handleBack}
            className="inline-flex items-center gap-2 bg-white hover:bg-gray-50 text-gray-700 hover:text-green-600 px-4 py-2 rounded-lg border border-gray-200 hover:border-green-300 transition-all duration-200 shadow-sm hover:shadow-md"
          >
            <ArrowLeft size={16} />
            <span className="font-medium">Back</span>
          </button>
        </div>

        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-green-100 to-emerald-100 px-4 sm:px-6 py-2 sm:py-3 rounded-full mb-4 sm:mb-6 border border-green-200/50 backdrop-blur-sm">
            <Cookie size={16} className="sm:w-4 sm:h-4 text-green-600" />
            <Badge className="bg-transparent text-green-700 px-0 py-0 text-xs sm:text-sm font-semibold">
              Cookie Policy
            </Badge>
          </div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-gray-900 mb-4 sm:mb-6 leading-tight">
            Cookie Policy
          </h1>
          <div className="flex items-center justify-center gap-4 text-sm sm:text-base text-gray-600">
            <div className="flex items-center gap-2">
              <Calendar size={14} className="sm:w-4 sm:h-4" />
              <span>Last updated: October 02, 2025</span>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto">
          <Card className="shadow-xl border-0 bg-white/90 backdrop-blur-sm">
            <CardHeader className="pb-4 p-4 sm:p-6">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-r from-green-500 to-emerald-500 rounded-lg sm:rounded-xl flex items-center justify-center">
                  <Building size={16} className="sm:w-5 sm:h-5 text-white" />
                </div>
                <CardTitle className="text-lg sm:text-xl lg:text-2xl font-bold text-gray-900">
                  Traffic Service International LLC
                </CardTitle>
              </div>
              <p className="text-gray-600 text-xs sm:text-sm">
                Building No: 915, office No: 579, CBD (Abu Dhabi bank) Al Falaj street, Muscat, Sultanate of Oman, Postal Code: 111
              </p>
            </CardHeader>
            <CardContent className="p-4 sm:p-6 space-y-6 sm:space-y-8">
              {/* Introduction */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">What Are Cookies</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-4">
                  Cookies are small text files that are placed on your computer, mobile device, or any other device by a website, containing the details of your browsing history on that website among its many uses.
                </p>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  This Cookie Policy explains how Traffic Service International LLC ("we", "us", or "our") uses cookies and similar technologies when you visit our website www.tsioman.com (the "Service").
                </p>
              </section>

              {/* How We Use Cookies */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">How We Use Cookies</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
                  We use cookies and similar tracking technologies to track the activity on our Service and store certain information. Tracking technologies we use include beacons, tags, and scripts to collect and track information and to improve and analyze our Service.
                </p>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some parts of our Service.
                </p>
              </section>

              {/* Types of Cookies */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Types of Cookies We Use</h2>
                
                <div className="space-y-4 sm:space-y-6">
                  {/* Necessary Cookies */}
                  <div className="p-4 sm:p-6 bg-blue-50/50 rounded-lg border border-blue-200/50">
                    <h3 className="text-base sm:text-lg font-semibold text-blue-800 mb-2 sm:mb-3">Necessary / Essential Cookies</h3>
                    <div className="space-y-2 text-sm sm:text-base">
                      <p><strong className="text-blue-700">Type:</strong> <span className="text-gray-700">Session Cookies</span></p>
                      <p><strong className="text-blue-700">Administered by:</strong> <span className="text-gray-700">Us</span></p>
                      <p><strong className="text-blue-700">Purpose:</strong> <span className="text-gray-700">These Cookies are essential to provide you with services available through the Website and to enable you to use some of its features. They help to authenticate users and prevent fraudulent use of user accounts. Without these Cookies, the services that you have asked for cannot be provided.</span></p>
                    </div>
                  </div>

                  {/* Cookies Policy Notice */}
                  <div className="p-4 sm:p-6 bg-green-50/50 rounded-lg border border-green-200/50">
                    <h3 className="text-base sm:text-lg font-semibold text-green-800 mb-2 sm:mb-3">Cookies Policy / Notice Acceptance Cookies</h3>
                    <div className="space-y-2 text-sm sm:text-base">
                      <p><strong className="text-green-700">Type:</strong> <span className="text-gray-700">Persistent Cookies</span></p>
                      <p><strong className="text-green-700">Administered by:</strong> <span className="text-gray-700">Us</span></p>
                      <p><strong className="text-green-700">Purpose:</strong> <span className="text-gray-700">These Cookies identify if users have accepted the use of cookies on the Website.</span></p>
                    </div>
                  </div>

                  {/* Functionality Cookies */}
                  <div className="p-4 sm:p-6 bg-purple-50/50 rounded-lg border border-purple-200/50">
                    <h3 className="text-base sm:text-lg font-semibold text-purple-800 mb-2 sm:mb-3">Functionality Cookies</h3>
                    <div className="space-y-2 text-sm sm:text-base">
                      <p><strong className="text-purple-700">Type:</strong> <span className="text-gray-700">Persistent Cookies</span></p>
                      <p><strong className="text-purple-700">Administered by:</strong> <span className="text-gray-700">Us</span></p>
                      <p><strong className="text-purple-700">Purpose:</strong> <span className="text-gray-700">These Cookies allow us to remember choices you make when you use the Website, such as remembering your login details or language preference. The purpose of these Cookies is to provide you with a more personal experience and to avoid you having to re-enter your preferences every time you use the Website.</span></p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Cookie Categories */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Cookie Categories</h2>
                
                <div className="space-y-4">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <h3 className="text-base font-semibold text-gray-800 mb-2">Session Cookies</h3>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                      Session Cookies are deleted as soon as you close your web browser. These are temporary cookies that are used to remember information during your browsing session.
                    </p>
                  </div>

                  <div className="border-l-4 border-green-500 pl-4">
                    <h3 className="text-base font-semibold text-gray-800 mb-2">Persistent Cookies</h3>
                    <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                      Persistent Cookies remain on your personal computer or mobile device when you go offline. These cookies are used to remember your preferences and improve your experience on future visits.
                    </p>
                  </div>
                </div>
              </section>

              {/* Web Beacons */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Web Beacons</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
                  Certain sections of our Service and our emails may contain small electronic files known as web beacons (also referred to as clear gifs, pixel tags, and single-pixel gifs) that permit the Company, for example, to count users who have visited those pages or opened an email and for other related website statistics.
                </p>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  Web beacons help us understand how users interact with our Service and emails, allowing us to improve our content and user experience.
                </p>
              </section>

              {/* Managing Cookies */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Managing Your Cookie Preferences</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
                  You have the right to decide whether to accept or reject cookies. You can exercise your cookie rights by setting your preferences in the cookie settings or by modifying your web browser controls.
                </p>
                
                <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-2 sm:mb-3">Browser Settings</h3>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
                  Most web browsers allow you to control cookies through their settings preferences. However, limiting cookies may impact your experience on our website.
                </p>
                
                <div className="bg-yellow-50/50 border border-yellow-200/50 rounded-lg p-4 sm:p-6">
                  <h4 className="text-sm sm:text-base font-semibold text-yellow-800 mb-2">Important Note</h4>
                  <p className="text-yellow-700 text-sm sm:text-base leading-relaxed">
                    If you choose to reject cookies, you may not be able to use some parts of our Service. Some features may not function properly without cookies.
                  </p>
                </div>
              </section>

              {/* Third-Party Cookies */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Third-Party Cookies</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
                  Our Service may contain links to other websites that are not operated by us. If you click on a third-party link, you will be directed to that third party's site.
                </p>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  We strongly advise you to review the Cookie Policy of every site you visit. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party sites or services.
                </p>
              </section>

              {/* Updates to Cookie Policy */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Updates to This Cookie Policy</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
                  We may update our Cookie Policy from time to time. We will notify you of any changes by posting the new Cookie Policy on this page.
                </p>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  You are advised to review this Cookie Policy periodically for any changes. Changes to this Cookie Policy are effective when they are posted on this page.
                </p>
              </section>

              {/* Contact Information */}
              <section>
                <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Contact Us</h2>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed mb-3">
                  If you have any questions about this Cookie Policy, You can contact us:
                </p>
                <div className="flex items-center gap-3 p-4 bg-green-50/50 rounded-lg">
                  <Mail size={16} className="sm:w-5 sm:h-5 text-green-600" />
                  <span className="text-green-700 font-medium text-sm sm:text-base">{mockData.contact.email}</span>
                </div>
              </section>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};
